import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Container, 
  Typography, 
  Box, 
  Button, 
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Alert,
  TextField,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Divider,
} from '@mui/material';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { OrderData } from '../types/api';
import { createOrder, OrderResponse, getOrderById } from '../api/orders';
import { CartItem as CartItemComponent } from '../components/CartItem';
import OrderSummary from '../components/OrderSummary';
import { calculateCartItemFees, GST_TAX_ID, PST_TAX_ID, formatAttributesForSubmission, formatCurrency } from '../utils/pricing';
import { Address } from '../types/user';
import { addressApi } from '../api/auth';
import AddressSelector from '../components/user/AddressSelector';

// Interface for order details
interface OrderDetails {
  id: string;
  total: number;
  subtotal: number;
  tax: number;
  email: string;
  items: Array<{
  name: string;
  price: number;
  quantity: number;
    total: number;
    taxes?: Array<{
      name: string;
      value: number;
      total: number;
    }>;
  }>;
  paymentStatus: string;
  fulfillmentStatus: string;
  orderComments?: string;
  customSurcharges?: Array<{
    description: string;
    value: number;
    id: string;
    total: number;
  }>;
  shippingOption?: {
    shippingMethodName: string;
    shippingRate: number;
  };
  billingPerson?: {
    name: string;
    street: string;
    city: string;
    countryCode: string;
    postalCode: string;
    stateOrProvinceCode: string;
    phone: string;
    email: string;
  };
  shippingPerson?: {
    name: string;
    street: string;
    city: string;
    countryCode: string;
    postalCode: string;
    stateOrProvinceCode: string;
    phone: string;
    email: string;
  };
  createDate?: string;
  updateDate?: string;
}

const CartPage: React.FC = () => {
  const { items, removeFromCart, updateQuantity, clearCart, subtotal, itemCount, tax, total, deposit, ecoFee, gst, pst, deliveryFee } = useCart();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [orderSuccess, setOrderSuccess] = useState(false);
  const [orderNumber, setOrderNumber] = useState<string | null>(null);
  const [orderError, setOrderError] = useState<string | null>(null);
  const [checkoutDialogOpen, setCheckoutDialogOpen] = useState(false);
  const [orderDetails, setOrderDetails] = useState<OrderDetails | null>(null);
  const [isLoadingOrderDetails, setIsLoadingOrderDetails] = useState(false);
  
  // User information states
  const [email, setEmail] = useState(user?.email || '');
  const [name, setName] = useState('');
  const [street, setStreet] = useState('');
  const [city, setCity] = useState('Vancouver');
  const [postalCode, setPostalCode] = useState('');
  const [phone, setPhone] = useState('');
  const [province, setProvince] = useState('BC');
  const [deliveryNotes, setDeliveryNotes] = useState('');

  // Address selection state
  const [addressMode, setAddressMode] = useState<'select' | 'new'>('select');
  const [savedAddresses, setSavedAddresses] = useState<Address[]>([]);
  const [selectedAddressId, setSelectedAddressId] = useState<number | null>(null);
  const [loadingAddresses, setLoadingAddresses] = useState(false);

  // Add new address selection step
  const [checkoutStep, setCheckoutStep] = useState<'address' | 'details'>('address');
  const [selectedAddress, setSelectedAddress] = useState<Address | null>(null);

  // Fetch order details when we have an order number
  useEffect(() => {
    if (orderSuccess && orderNumber) {
      fetchOrderDetails(orderNumber);
    }
  }, [orderSuccess, orderNumber]);

  // Function to fetch order details from Ecwid
  const fetchOrderDetails = async (orderId: string) => {
    setIsLoadingOrderDetails(true);
    try {
      const response = await getOrderById(orderId);
      console.log("Order details fetched:", response);
      
      // Transform the response into our OrderDetails format
      if (response) {
        const orderData: OrderDetails = {
          id: response.orderNumber || response.id,
          total: response.total,
          subtotal: response.subtotal,
          tax: response.tax || 0,
          email: response.email,
          items: response.items?.map((item: { name: string; price: number; quantity: number; taxes?: any[] }) => ({
            name: item.name,
            price: item.price,
            quantity: item.quantity,
            total: item.price * item.quantity,
            taxes: item.taxes
          })) || [],
          paymentStatus: response.paymentStatus,
          fulfillmentStatus: response.fulfillmentStatus,
          orderComments: response.orderComments,
          customSurcharges: response.customSurcharges,
          shippingOption: response.shippingOption,
          createDate: response.createDate,
          updateDate: response.updateDate
        };
        
        setOrderDetails(orderData);
      }
    } catch (error) {
      console.error("Error fetching order details:", error);
    } finally {
      setIsLoadingOrderDetails(false);
    }
  };

  const handleCheckoutOpen = () => {
    if (!user) {
      // Redirect to login if not authenticated
      navigate('/login', { state: { from: '/cart' } });
      return;
    }
    setCheckoutDialogOpen(true);
    setCheckoutStep('address');
    setSelectedAddress(null);
  };

  const handleCheckoutClose = () => {
    setCheckoutDialogOpen(false);
    setCheckoutStep('address');
    setSelectedAddress(null);
  };

  const handleAddressSelected = (address: Address) => {
    setSelectedAddress(address);
    // Fill in the address fields
    setName(address.name);
    setStreet(address.street);
    setCity(address.city);
    setProvince(address.state_or_province_code || 'BC');
    setPostalCode(address.postal_code);
    setPhone(address.phone || '');
    setDeliveryNotes(address.delivery_notes || '');
    
    // Move to the next step
    setCheckoutStep('details');
  };

  const handleCheckout = async () => {
    // Validate user input
    if (!email || !name || !street || !city || !postalCode || !phone) {
      setOrderError('Please fill in all required fields.');
      return;
    }

    setIsSubmitting(true);
    setOrderError(null);

    try {
      // Format the monetary values to prevent floating point issues
      const formattedSubtotal = Number(subtotal.toFixed(2));
      const formattedTotal = Number(total.toFixed(2));
      const formattedTax = Number(tax.toFixed(2));
      const formattedGST = Number(gst.toFixed(2));
      const formattedPST = Number(pst.toFixed(2));
      const formattedDeposit = Number(deposit.toFixed(2));
      const formattedEcoFee = Number(ecoFee.toFixed(2));
      const formattedDeliveryFee = Number(deliveryFee.toFixed(2));
      
      console.log('Cart totals before order creation:', { 
        subtotal, 
        total, 
        tax,
        gst,
        pst,
        deposit,
        ecoFee,
        deliveryFee,
        formattedSubtotal,
        formattedTotal,
        formattedTax,
        formattedGST,
        formattedPST,
        formattedDeposit,
        formattedEcoFee,
        formattedDeliveryFee
      });
      
      const orderData: OrderData = {
        email,
        subtotal: formattedSubtotal,
        total: formattedTotal,
        tax: formattedTax,
        paymentStatus: "AWAITING_PAYMENT",
        fulfillmentStatus: "AWAITING_PROCESSING",
        items: items.map(item => {
          const fees = calculateCartItemFees(item);
          const gst_rate = 5; // 5% GST
          const pst_rate = 7; // 7% PST
          
          // Determine if PST applies to this item
          const hasPST = fees.pst > 0;
          
          // Create tax array with GST (and PST if applicable)
          const taxes = [
            {
              name: "GST",
              value: gst_rate,
              total: Number(fees.gst.toFixed(2)),
              taxOnDiscountedSubtotal: Number(fees.gst.toFixed(2)),
              taxOnShipping: 0,
              includeInPrice: false,
              sourceTaxRateId: GST_TAX_ID,
              sourceTaxRateType: "MANUAL"
            }
          ];
          
          // Add PST if applicable
          if (hasPST) {
            taxes.push({
              name: "PST",
              value: pst_rate,
              total: Number(fees.pst.toFixed(2)),
              taxOnDiscountedSubtotal: Number(fees.pst.toFixed(2)),
              taxOnShipping: 0,
              includeInPrice: false,
              sourceTaxRateId: PST_TAX_ID, 
              sourceTaxRateType: "MANUAL"
            });
          }
          
          return {
            productId: Number(item.productId || item.id),
            name: item.name,
            price: Number(item.price.toFixed(2)),
            productPrice: Number(item.price.toFixed(2)),
            priceWithoutTax: Number(item.price.toFixed(2)),
            quantity: item.quantity,
            shortDescription: item.description || "",
            sku: item.sku || "",
            weight: item.weight || 0,
            taxes: taxes,
            tax: Number(fees.tax.toFixed(2)),
            taxable: true,
            attributes: formatAttributesForSubmission(item.product?.attributes || [])
          };
        }),
        billingPerson: {
          name,
          street,
          city,
          countryCode: 'CA',
          postalCode,
          stateOrProvinceCode: province,
          phone,
          email
        },
        shippingPerson: {
          name,
          street,
          city,
          countryCode: 'CA',
          postalCode,
          stateOrProvinceCode: province,
          phone,
          email
        },
        paymentMethod: "Manual",
        shippingOption: {
          shippingMethodName: formattedDeliveryFee > 0 ? "Standard Delivery" : "Free Delivery",
          shippingRate: formattedDeliveryFee
        },
        orderComments: deliveryNotes || '',
        refererId: "", // Optional tracking code
        disableAllCustomerNotifications: false,
        totalAndTax: {
          subtotal: formattedSubtotal,
          discount: 0,
          shipping: formattedDeliveryFee,
          tax: formattedTax,
          total: formattedTotal
        },
        extraFields: {
          "order_notes": deliveryNotes || "None",
          "preferred_delivery_time": "Afternoon"
        },
        // Add custom surcharges for bottle deposit and eco fee
        customSurcharges: [
          {
            id: "cstmz_bottle_deposit_surcharge",
            value: formattedDeposit,
            type: "ABSOLUTE",
            total: formattedDeposit,
            totalWithoutTax: formattedDeposit,
            description: "Bottle Deposit",
            descriptionTranslated: "",
            taxable: false,
            taxes: []
          },
          {
            id: "cstmz_eco_fee_surcharge",
            value: formattedEcoFee,
            type: "ABSOLUTE",
            total: formattedEcoFee,
            totalWithoutTax: formattedEcoFee,
            description: "Eco Fee",
            descriptionTranslated: "",
            taxable: false,
            taxes: []
          }
        ]
      };

      // Log the order data for debugging
      console.log('Sending order data:', JSON.stringify(orderData, null, 2));

      const headers = {
        'Content-Type': 'application/json',
        'Accept': '*/*',
        'User-Agent': 'curl/8.7.1'
      };

      try {
        const response = await createOrder(orderData, headers);
        console.log('Order created successfully:', response);
        
        // Store the order number for confirmation
        if (response && response.orderId) {
          setOrderNumber(response.orderId);
        }
        
        // Show success and clear cart
        setOrderSuccess(true);
        clearCart();
        setCheckoutDialogOpen(false);
      } catch (apiError: any) {
        console.error('API Error creating order:', apiError);
        let errorMessage = 'Could not create order. Please try again or contact support.';
        
        if (apiError.response && apiError.response.data && apiError.response.data.detail) {
          errorMessage = apiError.response.data.detail;
        }
        
        setOrderError(errorMessage);
        throw apiError;
      }
      
    } catch (error) {
      console.error('Error creating order:', error);
      setOrderError('Failed to create order. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    // Set email from user account if logged in
    if (user) {
      setEmail(user.email);
    }
    
    // Fetch user's saved addresses when dialog opens
    if (checkoutDialogOpen) {
      fetchUserAddresses();
    }
  }, [user, checkoutDialogOpen]);
  
  const fetchUserAddresses = async () => {
    if (!user) return;
    
    try {
      setLoadingAddresses(true);
      const addresses = await addressApi.getAddresses();
      setSavedAddresses(addresses);
      
      // Select default address if available
      const defaultAddress = addresses.find(addr => addr.is_default);
      if (defaultAddress) {
        setSelectedAddressId(defaultAddress.id);
        fillAddressForm(defaultAddress);
      } else if (addresses.length > 0) {
        setSelectedAddressId(addresses[0].id);
        fillAddressForm(addresses[0]);
      }
    } catch (error) {
      console.error('Error fetching addresses:', error);
    } finally {
      setLoadingAddresses(false);
    }
  };
  
  const fillAddressForm = (address: Address) => {
    setName(address.name);
    setStreet(address.street);
    setCity(address.city);
    setProvince(address.state_or_province_code || 'BC');
    setPostalCode(address.postal_code);
    setPhone(address.phone || '');
    setDeliveryNotes(address.delivery_notes || '');
  };
  
  const handleAddressChange = (addressId: number) => {
    const address = savedAddresses.find(addr => addr.id === addressId);
    if (address) {
      setSelectedAddressId(addressId);
      fillAddressForm(address);
    }
  };
  
  const handleAddressTabChange = (_event: React.SyntheticEvent, newValue: 'select' | 'new') => {
    setAddressMode(newValue);
    
    // Clear form if switching to new address
    if (newValue === 'new') {
      setName('');
      setStreet('');
      setCity('');
      setProvince('BC');
      setPostalCode('');
      setPhone('');
      setDeliveryNotes('');
      setSelectedAddressId(null);
    } else if (savedAddresses.length > 0) {
      // Select first address again when switching back
      const defaultAddr = savedAddresses.find(addr => addr.is_default) || savedAddresses[0];
      setSelectedAddressId(defaultAddr.id);
      fillAddressForm(defaultAddr);
    }
  };

  if (orderSuccess) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Paper 
          elevation={3} 
          sx={{ 
            p: 4, 
            textAlign: 'center',
            borderRadius: 2
          }}
        >
          <Box sx={{ mb: 3 }}>
            <Typography variant="h4" color="primary" gutterBottom>
              Order Placed Successfully!
            </Typography>
            
            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
              Order Number: #{orderNumber}
            </Typography>
            
            <Typography variant="body1" paragraph sx={{ mt: 2 }}>
              Thank you for your order. We've sent a confirmation to {email}.
            </Typography>
            
            {isLoadingOrderDetails ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                <CircularProgress />
              </Box>
            ) : orderDetails ? (
              <Box sx={{ mt: 4, textAlign: 'left' }}>
                <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                  Order Details
                </Typography>
                
                <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
                    {/* hiding the payment and fulfillment status for now, this would be used when a user comes back to see their order status */}
                    {/* <Box sx={{ width: '50%', px: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        Payment Status
                      </Typography>
                      <Chip 
                        label={orderDetails.paymentStatus.replace(/_/g, ' ')} 
                        color={orderDetails.paymentStatus === 'PAID' ? 'success' : 'warning'}
                        size="small"
                        sx={{ mt: 0.5 }}
                      />
                    </Box>
                    <Box sx={{ width: '50%', px: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        Fulfillment Status
                      </Typography>
                      <Chip 
                        label={orderDetails.fulfillmentStatus.replace(/_/g, ' ')} 
                        color={orderDetails.fulfillmentStatus === 'DELIVERED' ? 'success' : 'info'}
                        size="small"
                        sx={{ mt: 0.5 }}
                      />
                    </Box> */}
                    {orderDetails.createDate && (
                      <Box sx={{ width: '100%', mt: 2, px: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          Order Date
                        </Typography>
                        <Typography variant="body2">
                          {new Date(orderDetails.createDate).toLocaleDateString('en-US', {
                            year: 'numeric', 
                            month: 'long', 
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Paper>
                
                {/* Shipping Address */}
                {orderDetails.shippingPerson && (
                  <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                    <Paper variant="outlined" sx={{ p: 2, flex: 1 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Shipping Address
                      </Typography>
                      <Typography variant="body2">
                        {orderDetails.shippingPerson.name}
                      </Typography>
                      <Typography variant="body2">
                        {orderDetails.shippingPerson.street}
                      </Typography>
                      <Typography variant="body2">
                        {orderDetails.shippingPerson.city}, {orderDetails.shippingPerson.stateOrProvinceCode} {orderDetails.shippingPerson.postalCode}
                      </Typography>
                      <Typography variant="body2">
                        {orderDetails.shippingPerson.countryCode}
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        {orderDetails.shippingPerson.phone}
                      </Typography>
                    </Paper>
                    
                    {orderDetails.orderComments && (
                      <Paper variant="outlined" sx={{ p: 2, flex: 1 }}>
                        <Typography variant="subtitle2" gutterBottom>
                          Order Notes
                        </Typography>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                          {orderDetails.orderComments}
                        </Typography>
                      </Paper>
                    )}
                  </Box>
                )}
                
                <Typography variant="subtitle2" gutterBottom>
                  Items
                </Typography>
                
                <List sx={{ mb: 3, bgcolor: 'background.paper', border: '1px solid #eee', borderRadius: 1 }}>
                  {orderDetails.items.map((item, index) => (
                    <React.Fragment key={index}>
                      <ListItem>
                        <ListItemText
                          primary={item.name}
                          secondary={`${item.quantity} × ${formatCurrency(item.price)}`}
                        />
                        <Typography variant="body2">
                          {formatCurrency(item.total)}
                        </Typography>
                      </ListItem>
                      {index < orderDetails.items.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
                
                <Box sx={{ mb: 3, border: '1px solid #eee', borderRadius: 1, p: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Price Summary
                  </Typography>
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">Subtotal</Typography>
                    <Typography variant="body2">{formatCurrency(orderDetails.subtotal)}</Typography>
                  </Box>
                  
                  {orderDetails.tax > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">Tax</Typography>
                      <Typography variant="body2">{formatCurrency(orderDetails.tax)}</Typography>
                    </Box>
                  )}
                  
                  {orderDetails.customSurcharges?.map((surcharge, index) => (
                    <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">{surcharge.description}</Typography>
                      <Typography variant="body2">{formatCurrency(surcharge.value)}</Typography>
                    </Box>
                  ))}
                  
                  {orderDetails.shippingOption && orderDetails.shippingOption.shippingRate > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">Shipping</Typography>
                      <Typography variant="body2">{formatCurrency(orderDetails.shippingOption.shippingRate)}</Typography>
                    </Box>
                  )}
                  
                  <Divider sx={{ my: 1 }} />
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="subtitle2">Total</Typography>
                    <Typography variant="subtitle2">{formatCurrency(orderDetails.total)}</Typography>
                  </Box>
                </Box>
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
                Order details are being processed...
              </Typography>
            )}
            
            <Button 
              variant="contained" 
              size="large"
              component={Link}
              to="/"
              sx={{ mt: 3 }}
            >
              Continue Shopping
            </Button>
          </Box>
        </Paper>
      </Container>
    );
  }

  if (items.length === 0) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Paper 
          elevation={0} 
          sx={{ 
            p: 4, 
            textAlign: 'center',
            border: '1px solid #eee',
            borderRadius: 2
          }}
        >
          <ShoppingCartIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            Your cart is empty
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            Looks like you haven't added any products to your cart yet.
          </Typography>
          <Button 
            variant="contained"
            component={Link}
            to="/"
            sx={{ mt: 2 }}
          >
            Start Shopping
          </Button>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4, minHeight: 'calc(100vh - 160px)' }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Your Shopping Cart
      </Typography>
      
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '8fr 4fr' }, gap: 4 }}>
        {/* Cart Items */}
        <Box>
          <Paper elevation={0} sx={{ border: '1px solid #eee', borderRadius: 2, p: 2 }}>
            {items.map((item) => (
              <CartItemComponent
                key={item.id}
                id={item.id}
                name={item.name}
                price={item.price}
                quantity={item.quantity}
                image={item.image}
                onRemove={removeFromCart}
                onQuantityChange={updateQuantity}
              />
            ))}
          </Paper>
        </Box>
        
        {/* Order Summary */}
        <Box>
          <OrderSummary 
            onCheckout={handleCheckoutOpen}
            checkoutButtonText="Proceed to Checkout"
          />
          
          {/* Free shipping threshold message */}
          <Box 
            sx={{ 
              mt: 2, 
              border: '1px dashed', 
              borderColor: 'primary.light', 
              p: 3, 
              borderRadius: 1,
              bgcolor: 'rgba(46, 125, 50, 0.05)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center'
            }}
          >
            <Typography variant="body2" color="primary.main" fontWeight={500} fontSize="0.95rem">
              Free shipping on orders over $100
            </Typography>
          </Box>
        </Box>
      </Box>
      
      {/* Checkout Dialog */}
      <Dialog 
        open={checkoutDialogOpen} 
        onClose={isSubmitting ? undefined : handleCheckoutClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle style={{ fontWeight: 'bold', fontSize: '1.5rem', color: 'text.primary' }}>
          {checkoutStep === 'address' ? 'Select Delivery Address' : 'Complete Your Order'}
        </DialogTitle>
        <DialogContent>
          {orderError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {orderError}
            </Alert>
          )}
          
          {checkoutStep === 'address' ? (
            <AddressSelector 
              onAddressSelected={handleAddressSelected}
              onCancel={handleCheckoutClose}
              buttonText="Continue to Order Details"
            />
          ) : (
            <>
              <DialogContentText sx={{ mb: 2 }}>
                Please review your order details below.
              </DialogContentText>
              
              {/* Show selected address */}
              {selectedAddress && (
                <Paper 
                  variant="outlined" 
                  sx={{ p: 2, mb: 3, backgroundColor: 'background.paper' }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                      Delivery Address
                    </Typography>
                    <Button 
                      size="small"
                      onClick={() => setCheckoutStep('address')}
                    >
                      Change
                    </Button>
                  </Box>
                  
                  <Box sx={{ color: 'text.primary', fontSize: '0.9rem' }}>
                    <div>{selectedAddress.name}</div>
                    <div>{selectedAddress.street}</div>
                    <div>{selectedAddress.city}, {selectedAddress.state_or_province_code} {selectedAddress.postal_code}</div>
                    <div>{selectedAddress.country_code === 'CA' ? 'Canada' : 'United States'}</div>
                    {selectedAddress.phone && <div>Phone: {selectedAddress.phone}</div>}
                  </Box>
                </Paper>
              )}
              
              <TextField
                label="Email"
                type="email"
                required
                fullWidth
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                margin="normal"
              />
              
              <TextField
                label="Delivery Notes"
                fullWidth
                multiline
                rows={3}
                value={deliveryNotes}
                onChange={(e) => setDeliveryNotes(e.target.value)}
                margin="normal"
                placeholder="Special delivery instructions, gate codes, preferred time, etc."
                helperText="Optional: Add any special instructions for delivery"
              />
            </>
          )}
        </DialogContent>
        {checkoutStep === 'details' && (
          <DialogActions sx={{ px: 3, pb: 3 }}>
            <Button 
              onClick={() => setCheckoutStep('address')} 
              color="inherit"
              disabled={isSubmitting}
            >
              Back
            </Button>
            <Button 
              onClick={handleCheckout} 
              variant="contained"
              color="primary"
              disabled={isSubmitting || !email}
              startIcon={isSubmitting && <CircularProgress size={16} color="inherit" />}
            >
              {isSubmitting ? 'Processing...' : 'Place Order'}
            </Button>
          </DialogActions>
        )}
      </Dialog>
    </Container>
  );
};

export default CartPage; 